<script setup>
import { useCustomerStore } from "@/stores/CustomersStore"
import { useDisplay } from "vuetify"
import { useCalendarStore } from "@/views/apps/calendar/useCalendarStore"
import { useEventStore } from "@/stores/EventsStore"
import { useI18n } from "vue-i18n"
import { extractDate } from "@/utils/datetime"
import { formatNumber } from "@/utils/strings"
import { showWarningToast } from "@/utils/toast"

const props = defineProps({
  allowVoucherUse: {
    type: Boolean,
    default: false,
  },
  allowPassUse: {
    type: Boolean,
    default: false,
  },
})

const calendarStore = useCalendarStore()
const eventStore = useEventStore()
const customerStore = useCustomerStore()
const { mobile } = useDisplay()
const { t } = useI18n()

//TODO: Change title to be  displayed according to type (voucher/pass)
//TODO: Remove extended row
const headers = [
  { title: t('theName'), key: 'name', align: 'start', sortable: false, maxWidth: '125px' },
  // { title: t('type'), key: 'type', align: 'start', sortable: false, maxWidth: '115px' },
  { title: t('code'), key: 'identifier', align: 'end', sortable: false, maxWidth: '115px' },
  { title: t('validUntilShort'), key: 'timeout', align: 'center',  sortable: false, maxWidth: '135px' },
  { title: t('availableTotalValue'), key: 'value', align: 'end', sortable: false  },
]

const useVoucher = (type, voucherIdentifier) => {
  const methodName = type === 'pass' ? 'pass_saloner' : 'bon_saloner'

  // check if voucherIdentifier is already in use
  if (eventStore.selectedPaymentMethods.some(m => m.voucher?.identifier === voucherIdentifier)) {
    showWarningToast(t('selected_code_is_already_in_use'))

    return
  }

  let methodCurrentIndex = eventStore.selectedPaymentMethods.findIndex(m => m.name === methodName && m.voucher?.identifier?.length === 0)
  let method = eventStore.selectedPaymentMethods[methodCurrentIndex]

  if(!method) {
    methodCurrentIndex = eventStore.selectedPaymentMethods.length
    method = JSON.parse(JSON.stringify(calendarStore.paymentMethods.find(m => m.name === methodName )))
    method.amount = 0
    eventStore.selectedPaymentMethods.push(method)
  }
  method.voucher.identifier = voucherIdentifier
  //method.voucher.voucherType = type === 'pass' ? 2 : 1

  nextTick(() => {
    const input = document.getElementById(`${methodName}_input_${methodCurrentIndex}`)
    if(input) input.dispatchEvent(new Event('input', { bubbles: true }))
  })
}
</script>

<template>
  <VDataTable
    v-if="customerStore.customerVouchersAndTickets"
    id="vouchersAndTicketsCombined"
    class="custom-table xs-font-size"
    :density="mobile ? 'compact' : 'comfortable'"
    :class="!mobile ? 'limit-height' : ''"
    :items-per-page="-1"
    :headers="headers"
    fixed-header
    hover
    :items="customerStore.customerVouchersAndTickets"
    :mobile="mobile"
    hide-default-footer
    :expanded="customerStore.customerVouchersAndTickets.map(ticket => ticket.id)"
  >
    <template #item.name="{ item }">
      <div class="text-high-emphasis font-weight-bold line-height-normal">
        <VChip size="x-small">
          {{ item?.type === 'voucher' ? $t('voucher') : $t('pass') }}
        </VChip>
        <span class="ml-1 text-break">{{ item?.name || '-' }}</span>
      </div>
    </template>
    <template #item.type="{ item }">
      <div class="font-weight-medium">
        {{ item?.type === 'voucher' ? $t('voucher') : $t('pass') }}
      </div>
    </template>
    <template #item.identifier="{ item }">
      <div class="d-flex align-center justify-end">
        <span v-if="item.type === 'voucher' && props.allowVoucherUse && eventStore.showFinalization && eventStore.allowSave">
          <VIcon
            icon="ri-checkbox-circle-line"
            color="info"
            class="mr-1"
            @click="useVoucher(item?.type, item?.identifier)"
          />
          <VTooltip activator="parent">
            {{ $t('use_this_code') }}
          </VTooltip></span>
        <span v-if="item.type === 'pass' && props.allowPassUse && eventStore.showFinalization && eventStore.allowSave">
          <VIcon
            icon="ri-checkbox-circle-line"
            color="info"
            class="mr-1"
            @click="useVoucher(item?.type, item?.identifier)"
          />
          <VTooltip activator="parent">
            {{ $t('use_this_code') }}
          </VTooltip></span>
        <span>{{ item?.identifier }}</span>
      </div>
    </template>
    <template #item.timeout="{ item }">
      <VChip size="x-small">
        {{ extractDate(new Date(item?.timeout) || '-') }}
      </VChip>
    </template>
    <template #item.value="{ item }">
      <div>
        <span v-if="item?.type=== 'pass'">
          - /
        </span>
        <span v-else-if="item?.availableValue && item?.type !== 'pass'">
          {{ formatNumber(item.availableValue) }} /
        </span>
        <span>
          {{ formatNumber(item?.value) }}&nbsp;{{ calendarStore.currency }}
        </span>
      </div>
    </template>
    <template #expanded-row="{ item, columns }">
      <tr v-if="item.services">
        <td
          :colspan="columns.length"
          class="expanded-row-cell"
        >
          <VList
            density="compact"
            class="xs-font-size pt-0 rounded-lg "
            :class="mobile ? 'mb-4' : ''"
          >
            <VListItem
              v-for="(service, index) in item.services"
              :key="service.serviceId || index"
              density="compact"
              class="py-0 px-0 px-md-4 "
            >
              <template #prepend>
                <VIcon
                  size="16"
                  icon="ri-arrow-right-line"
                />
              </template>
              <VListItemTitle class="text-medium-emphasis xs-font-size text-no-truncate lh-1 mt-1">
                <span :class="[{'mr-1' : service?.isDeleted}]">
                  {{ service?.name || '-' }}
                </span>
                <VChip
                  v-if="service?.isDeleted"
                  variant="tonal"
                  color="error"
                  size="x-small"
                  label
                >
                  {{ $t('deleted_service') }}
                </VChip>
              </VListItemTitle>
              <template #append>
                <VChip size="x-small">
                  {{ $t('usesLeft') }} {{ service?.usesLeft || '-' }}/{{ service?.uses || '-' }}
                </VChip>
              </template>
            </VListItem>
          </VList>
        </td>
      </tr>
    </template>
  </VDataTable>
  <div
    v-else
    class="d-flex flex-column align-center justify-center mt-4 xs-font-size"
  >
    <div class="d-flex align-center ga-2 ">
      <VIcon
        icon="ri-information-line"
        size="small"
        color="grey-lighten-1"
      />
      <div class=" text-medium-emphasis">
        {{ $t('no_vouchers_or_tickets') }}
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.user-vouchers-holder {
  .v-expansion-panel-text__wrapper {
    padding-inline: 0 !important;
  }

  .v-list-item {
    border-radius: 8px;
  }

  .gap-y-1 {
    row-gap: 4px;
  }
}

#vouchersAndTicketsCombined {
  @media (min-width: 1280px) {
    th,td {
      padding-inline: 0.1rem;
    }
  }
}

.limit-height {
  max-height: 500px;
  overflow-y: auto;
}
// Hide border between expanded row and previous row
.expanded-row-cell {
  position: relative;
  &::after {
    content: '';
    position: absolute;
    top: -1px;
    left: 0;
    width: 100%;
    height: 8px;
    background-color:rgb(var(--v-theme-surface));
    z-index: 1;
  }
}


.v-data-table__tr.v-data-table__tr--mobile {

  .v-data-table__td:first-child {
    .v-data-table__td-title,
    .v-data-table__td-value{
      margin-top: 20px;
      margin-bottom: 5px;
    }
  }
  .v-data-table__td:last-child {
    .v-data-table__td-title,
    .v-data-table__td-value{
      margin-bottom: 20px;
    }
  }
}
</style>
